import { useMemo, useCallback, useState } from 'react';
import { useQueryStates, parseAsInteger, parseAsString } from 'nuqs';
import { Link } from 'react-router-dom';
import {
  ExternalConnectionLimitedDto,
  ExternalConnectionType,
} from '@/lib/integrationTypes/externalConnectionTypes';
import { Time } from '@/components/ui/time';
import { EarthLock, Siren, Plug } from 'lucide-react';
import { ColumnDef } from '@tanstack/react-table';
import { Comparator, FilterOperator, FilterGroup } from '@/lib/utils';
import {
  DataTable,
  DataTableContainer,
  DataTableContent,
  DataTableFilterBar,
  DataTableHeader,
  DataTableRows,
  DataTableWrapper,
  DataTableFooter,
  DataTableUtilityBar,
} from '@/components/data-table';
import {
  FilterField,
  FilterForm,
  FilterHeader,
  FilterInlineResetButton,
} from '@/components/filters/FilterForm';
import { ScrollArea } from '@/components/ui/scroll-area';

interface ConnectionsTableProps {
  connections: ExternalConnectionLimitedDto[];
  isLoading: boolean;
  headerActions?: React.ReactNode;
}

interface ConnectionTableData {
  id: string;
  name: string;
  description: string;
  type: ExternalConnectionType;
  integrationName: string;
  integrationBrand: string;
  created: string;
  lastScanTime: string | null;
  availableDeviceCount: string;
}

function extractConnectionTableData(connections: ExternalConnectionLimitedDto[]): ConnectionTableData[] {
  return connections.map((connection) => ({
    id: connection.id || '',
    name: connection.name,
    description: connection.description,
    type: connection.type,
    integrationName: connection.integration?.name || '',
    integrationBrand: connection.integration?.brand || '',
    created: connection.created,
    lastScanTime: connection.lastScanTime,
    availableDeviceCount: connection.availableDeviceCount,
  }));
}

// URL param parsers for connections filters
const connectionsUrlParsers = {
  connectionsPage: parseAsInteger.withDefault(1),
  connectionsPageSize: parseAsInteger.withDefault(20),
  search: parseAsString,
  filters: parseAsString,
};

export function ConnectionsTable({ connections, isLoading, headerActions }: ConnectionsTableProps) {
  const [urlParams, setUrlParams] = useQueryStates(connectionsUrlParsers, { history: 'replace' });

  // Derive values from URL
  const page = urlParams.connectionsPage;
  const pageSize = urlParams.connectionsPageSize;
  const searchQuery = urlParams.search ?? '';
  const filtersOpen = urlParams.filters === 'open';

  const filteredConnections = useMemo(() => {
    if (!searchQuery) return connections;
    return connections.filter((connection) => {
      return (
        connection.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        connection.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        connection.integration?.name?.toLowerCase()?.includes(searchQuery.toLowerCase()) ||
        connection.integration?.brand?.toLowerCase()?.includes(searchQuery.toLowerCase())
      );
    });
  }, [connections, searchQuery]);

  const connectionColumns: ColumnDef<ConnectionTableData>[] = useMemo(() => [
    {
      id: 'name',
      accessorKey: 'name',
      enableHiding: false,
      enableSorting: true,
      meta: { label: 'Name' },
      header: 'Name',
      cell: ({ row }) => {
        const { name, id, type } = row.original;
        const Icon = type === ExternalConnectionType.Vms ? EarthLock : Siren;
        return (
          <Link
            to={`/connections/${id}`}
            className="flex items-center gap-2 font-semibold text-lime hover:underline"
          >
            <Icon className="h-5 w-5" />
            {name}
          </Link>
        );
      },
    },
    {
      id: 'description',
      accessorKey: 'description',
      enableSorting: false,
      meta: { label: 'Description' },
      header: 'Description',
      cell: ({ row }) => <span>{row.original.description}</span>,
    },
    {
      id: 'type',
      accessorKey: 'type',
      enableSorting: false,
      meta: { label: 'Type' },
      header: 'Type',
      cell: ({ row }) => {
        const type = row.original.type;
        return (
          <span>
            {type === ExternalConnectionType.Vms
              ? 'VMS'
              : type === ExternalConnectionType.ExternalService
                ? 'External Service'
                : type}
          </span>
        );
      },
    },
    {
      id: 'integrationName',
      accessorKey: 'integrationName',
      enableSorting: false,
      meta: { label: 'Integration Name' },
      header: 'Integration Name',
      cell: ({ row }) => <span>{row.original.integrationName}</span>,
    },
    {
      id: 'integrationBrand',
      accessorKey: 'integrationBrand',
      enableSorting: false,
      meta: { label: 'Integration Brand' },
      header: 'Integration Brand',
      cell: ({ row }) => (
        <span className="text-gray-400">{row.original.integrationBrand}</span>
      ),
    },
    {
      id: 'created',
      accessorKey: 'created',
      enableSorting: true,
      meta: { label: 'Created' },
      header: 'Created',
      cell: ({ row }) => {
        const created = row.original.created;
        return (
          <span className="text-gray-400">
            <Time date={created} />
          </span>
        );
      },
    },
    {
      id: 'availableDeviceCount',
      accessorKey: 'availableDeviceCount',
      enableSorting: false,
      meta: { label: 'Available Devices' },
      header: 'Available Devices',
      cell: ({ row }) => <span>{row.original.availableDeviceCount}</span>,
    },
  ], []);

  const connectionData = useMemo(() => {
    return extractConnectionTableData(filteredConnections);
  }, [filteredConnections]);

  const countFilters = useCallback(
    (values: Record<string, string | Date | undefined>) =>
      Object.values(values).filter((v) => v !== undefined && v !== '').length,
    []
  );

  const handleFiltersChange = useCallback(
    ({
      values,
    }: {
      filterOperator: FilterOperator;
      filterFilterGroups: FilterGroup[];
      values: Record<string, string | Date | undefined>;
    }) => {
      setUrlParams({
        search: (values.search as string) || null,
        connectionsPage: 1,
      });
    },
    [setUrlParams]
  );

  const initialFilterCount = useMemo(
    () =>
      countFilters({
        search: searchQuery || undefined,
      }),
    [searchQuery, countFilters]
  );

  const [filterCount, setFilterCount] = useState(initialFilterCount);
  const filterScrollHeight = 'calc(100vh - 128px)';

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setUrlParams({ connectionsPageSize: newPageSize, connectionsPage: 1 });
  }, [setUrlParams]);

  // Calculate pagination for client-side data
  const totalCount = connectionData.length;
  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = connectionData.slice(startIndex, endIndex);

  return (
    <div className="relative">
      <DataTable
        columns={connectionColumns}
        data={paginatedData}
        loading={isLoading}
        error={null}
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        onPageChange={(p: number) => setUrlParams({ connectionsPage: p })}
        onPageSizeChange={handlePageSizeChange}
        pageSizeOptions={[10, 20, 50, 100]}
        stateKey="connections-table"
        emptyMessage="No connections found"
        sidebarOpen={filtersOpen}
        onSidebarOpenChange={(open) => setUrlParams({ filters: open ? 'open' : null })}
      >
        <FilterForm
          defaultValues={{ search: searchQuery || undefined }}
          onFiltersChange={({
            values,
          }: {
            filterOperator: FilterOperator;
            filterFilterGroups: FilterGroup[];
            values: Record<string, string | Date | undefined>;
          }) => {
            handleFiltersChange({ filterOperator: FilterOperator.And, filterFilterGroups: [], values });
            setFilterCount(countFilters(values));
            setUrlParams({
              search: (values.search as string) || null,
              connectionsPage: 1,
            });
          }}
        >
          <DataTableContainer className="w-full items-stretch md:gap-4 md:flex-nowrap">
            <DataTableFilterBar className="w-full shrink-0 md:w-[320px] md:flex-none md:shrink-0">
              <div className="flex flex-col">
                <FilterHeader onClose={() => setUrlParams({ filters: null })} />
                <ScrollArea style={{ height: filterScrollHeight }} className="flex-1 px-4">
                  <div className="space-y-4 pb-4 pt-2">
                    <FilterInlineResetButton
                      onReset={() => {
                        setUrlParams({
                          search: null,
                          connectionsPage: 1,
                        });
                        setFilterCount(0);
                      }}
                    />
                  </div>
                  <div className="space-y-4 pb-4">
                    <FilterField
                      type="text"
                      name="search"
                      label="Search Connections"
                      field="name"
                      comparator={Comparator.ILike}
                      placeholder="Search connections..."
                    />
                  </div>
                </ScrollArea>
              </div>
            </DataTableFilterBar>
            <DataTableContent className="w-full flex-1 min-w-0">
              <div data-onboarding="connections-utility-bar">
                <DataTableUtilityBar
                  showFilterToggle
                  showColumnVisibility
                  search={{
                    value: searchQuery,
                    onChange: (value) => setUrlParams({ search: value || null, connectionsPage: 1 }),
                    placeholder: 'Search connections...',
                  }}
                  filterCount={filterCount}
                  actions={<div data-onboarding="connections-add-button">{headerActions}</div>}
                />
              </div>
              <div data-onboarding="connections-table">
                <DataTableWrapper>
                  <DataTableHeader />
                  <DataTableRows />
                </DataTableWrapper>
              </div>
              <DataTableFooter />
            </DataTableContent>
          </DataTableContainer>
        </FilterForm>
      </DataTable>
    </div>
  );
}
